import ServicePageLayout from './ServicePageLayout';
import { IMG } from '@/content/site';

export default function AiMarketing() {
  return (
    <ServicePageLayout
      seo={{
        title: 'AI Marketing & Business Growth Services | KTK Advisors',
        description:
          'KTK Advisors helps businesses coordinate AI-supported marketing—prospect discovery, campaigns, creative, landing pages, and lead follow-up—with performance visibility.',
        path: '/solutions/ai-marketing',
      }}
      group="AI Marketing & Business Growth"
      headline="Bring your marketing, lead generation, and follow-up together."
      lede="Marketing that works is coordinated: the right audience, a clear campaign, an inquiry handled promptly, and results you can measure. KTK Advisors helps businesses explore AI-supported marketing activity—and keeps the whole loop visible in plain business language."
      audienceIntro="For businesses ready to grow on purpose."
      audience={[
        'Businesses that rely on referrals and want a predictable pipeline',
        'Companies without in-house marketing expertise',
        'Teams running activity that never seems to connect to results',
        'Owners who want one accountable point of contact for growth',
      ]}
      image={IMG.marketing}
      imageAlt="Marketing team collaborating around a laptop"
      problems={[
        {
          t: 'Marketing activity that never connects',
          d: 'An ad here, a post there, a landing page from last year—activity without coordination produces noise, not pipeline.',
        },
        {
          t: 'Inquiries that go cold',
          d: 'When follow-up is slow or inconsistent, the marketing spend behind the inquiry is wasted first.',
        },
        {
          t: 'Tools bought before strategy',
          d: 'AI tools promise a lot. Without a coordinated structure around audience, campaign, and follow-up, they automate noise.',
        },
        {
          t: 'No honest measurement',
          d: 'Without clear visibility into what produced an inquiry and what happened to it, every decision is a guess.',
        },
      ]}
      capabilities={[
        'AI-supported prospect discovery',
        'Advertising campaign coordination',
        'Creative production coordination',
        'Social content',
        'Landing page development',
        'Lead follow-up structure',
        'Performance measurement and reporting',
      ]}
      diagram={{
        title: 'One connected loop, from audience to measurement.',
        steps: [
          { n: '01', label: 'Audience' },
          { n: '02', label: 'Campaign' },
          { n: '03', label: 'Inquiry' },
          { n: '04', label: 'Follow-up' },
          { n: '05', label: 'Measurement' },
        ],
      }}
      diagramCaption="Each step feeds the next. The measurement loop is what separates marketing you can steer from marketing you can only hope about."
      helpsIntro="KTK Advisors coordinates the activity and translates the results—so growth conversations happen in business language, not jargon."
      helps={[
        {
          t: 'Start with the audience',
          d: 'Who you serve and how they find you shapes everything else. Strategy before tools, always.',
        },
        {
          t: 'Coordinate the pieces',
          d: 'Prospect discovery, campaigns, creative, content, landing pages, and follow-up work as one loop—coordinated under a single point of contact.',
        },
        {
          t: 'Keep the numbers visible',
          d: 'You see what ran, what it produced, and what happened to every inquiry. Clear reporting, in plain terms, on a rhythm you agree to.',
        },
      ]}
      faqs={[
        {
          q: 'Do I need to understand AI to do this?',
          a: 'No. KTK Advisors coordinates the activity and explains the results in clear business language. You make decisions with information you can act on.',
        },
        {
          q: 'Does this replace our marketing team or agency?',
          a: 'It depends on your setup. For some businesses KTK coordinates everything; for others it strengthens what an existing team or agency already runs.',
        },
        {
          q: 'Can you promise a number of leads?',
          a: 'No. Nobody honest promises lead volumes before understanding your market and offer. What you get instead is coordinated activity and measurement you can evaluate.',
        },
        {
          q: 'What does it cost?',
          a: 'Depending on scope, services may involve subscription, project, or service fees. Applicable terms are reviewed before an engagement begins.',
        },
        {
          q: 'How quickly will we see results?',
          a: 'That depends on your market, offer, and starting point. The first conversations establish realistic expectations—before anything is promised.',
        },
      ]}
      cta={{
        title: 'Discuss your growth goals.',
        copy: 'Tell us where growth should come from—and what has not worked so far. We\'ll help you shape a loop you can actually measure.',
        button: 'Discuss Your Growth Goals',
      }}
      interestParam="ai-marketing"
    />
  );
}
